import type { CSSProperties, SVGProps } from 'react'

// The one self-drawing stroke component (landing spec §3.1). Server-safe:
// no hooks; the caller supplies per-stroke `drawn` amounts (0..1). A stroke
// draws by dash offset and is hidden until its turn — round line caps on a
// zero-length dash would otherwise render as dots. Width is set in viewBox
// units; scaled layers divide it by their scale instead of using
// non-scaling-stroke, which pins the line to screen pixels and breaks
// pathLength normalisation.
export function StrokeDraw({
    strokePaths,
    viewBox,
    drawn,
    strokeWidth = 4.2,
    opacity,
    className,
    transition,
    style,
    ...rest
}: {
    strokePaths: string[]
    viewBox: string
    drawn: readonly number[]
    strokeWidth?: number
    /** Per-stroke opacity overriding the hidden-until-drawn rule, e.g. a
     *  carried stroke that fades in whole instead of drawing. */
    opacity?: ReadonlyArray<number | undefined>
    className?: string
    transition?: string
    style?: CSSProperties
} & Omit<
    SVGProps<SVGSVGElement>,
    'viewBox' | 'className' | 'style' | 'opacity'
>) {
    return (
        <svg
            viewBox={viewBox}
            className={className}
            style={style}
            aria-hidden="true"
            focusable="false"
            {...rest}
        >
            {strokePaths.map((d, i) => {
                const v = drawn[i] ?? 0
                return (
                    <path
                        key={i}
                        d={d}
                        pathLength={1}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                            strokeDasharray: 1,
                            strokeDashoffset: 1 - v,
                            opacity: opacity?.[i] ?? (v > 0 ? 1 : 0),
                            transition,
                        }}
                    />
                )
            })}
        </svg>
    )
}
